/**
 * Video (#334) for main's frontend, on the port's API.
 *
 * Main's video code talks to keyon: the uploader (src/api/videoUpload.js,
 * src/utils/videoUploader.js) to `video_upload.php?action=...`, the player
 * (AttachmentVideoPlayer.vue) to `file.php` by URL with the session cookie,
 * the probe and the download (src/api/keyonVideo.js) by fetch and by anchor.
 * None of that reaches the port: there is no keyon, no cookie, and a URL
 * cannot carry the bearer token. This module is the translation, kept in one
 * place so main's components stay main's:
 *
 *  - the uploader's six actions become the REST routes (pacmny-api
 *    VideoUploadController), answered with their REAL statuses and main's
 *    refusal body - `{status:'Error', code, message, error}` plus `reason`,
 *    `next_offset`, `retry_after`, `max_bytes` - because the uploader's retry
 *    logic reads them (409 asks where the session is, 410 starts again, 429
 *    waits, 401 mfa_required parks the upload). Never through the legacy
 *    bridge's handle(), which turns every refusal into a 200;
 *  - a chunk goes up as its raw bytes (`PUT .../chunks/{offset}`) with its
 *    digest in X-Chunk-SHA256, not as main's multipart form;
 *  - the discard is a keepalive fetch of DELETE with the bearer, so it still
 *    finishes when the page is going away;
 *  - the player gets a STREAM ticket (decision D5 as amended): one API URL a
 *    `<video>` element may read many slices from, short-lived, bound to the
 *    file and the person, and re-authorised on every slice by the API. It is
 *    never a storage URL, and never handed to anything but the element;
 *  - the probe asks the API, bearer in hand, for one byte, and the download is
 *    a single-use download ticket clicked as an anchor, so the browser streams
 *    the file to disk and never holds 100 MiB in a JavaScript Blob.
 *
 * Every request here goes through the API client's own axios instance with
 * `validateStatus: () => true`, so a refusal is read here and never reaches
 * the client's sign-in-again handling (an upload parks on mfa_required rather
 * than lose the message being composed).
 */
import axios from 'axios';
import api, { BASE_URL } from './client';
import oidc from '@/auth/oidcClient';
import { errorBodyOf } from './responseBody';
import { refusalStateFromResponse } from '@/api/keyonFiles';
import { isFileUid } from '@/utils/fileUrl';

// keyon/lib/UploadRefusal.php's sentence for a refusal that came with none.
const UNAVAILABLE_SENTENCE = 'Uploads are unavailable right now. Try again shortly.';

/** True when this axios request is addressed to keyon's video upload endpoint. */
export function isVideoUploadRequest(url) {
  return /\/video_upload\.php(\?|#|$)/.test(String(url || ''));
}

const UPLOAD_ID = /^[a-f0-9]{32}$/;

function query(url) {
  try {
    return new URL(String(url || ''), 'http://video-upload.invalid').searchParams;
  } catch (_) {
    return new URLSearchParams();
  }
}

function jsonBody(data) {
  if (data === null || data === undefined || data === '') return {};
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (_) {
      return {};
    }
  }
  return typeof data === 'object' && !(typeof FormData !== 'undefined' && data instanceof FormData) ? data : {};
}

/** axios's own rule: resolve when validateStatus accepts the status, else reject with the response. */
function settle(config, response) {
  const validate = config.validateStatus;
  if (!validate || validate(response.status)) return response;
  const error = new Error(`Request failed with status code ${response.status}`);
  error.config = config;
  error.response = response;
  error.request = null;
  error.isAxiosError = true;
  throw error;
}

function answer(config, status, data, headers = {}) {
  return settle(config, {
    data,
    status,
    statusText: status >= 200 && status < 300 ? 'OK' : '',
    headers: { 'content-type': 'application/json', 'x-bridged': '1', ...headers },
    config,
    request: null,
  });
}

/** Main's refusal body (keyon/lib/UploadRefusal.php body()), from the API's envelope. */
export function legacyVideoRefusal(body) {
  const error = (body && body.error) || {};
  const code = typeof error.code === 'string' ? error.code : 'uploads_unavailable';
  const message = typeof error.message === 'string' && error.message !== '' ? error.message : UNAVAILABLE_SENTENCE;
  const out = { status: 'Error', code, message, error: message };
  const details = error.details && typeof error.details === 'object' ? error.details : {};
  ['reason', 'next_offset', 'retry_after', 'max_bytes'].forEach((key) => {
    if (details[key] !== undefined && out[key] === undefined) out[key] = details[key];
  });
  return out;
}

/** A request the bridge could not make at all: what axios reports for a network failure. */
function noAnswer(config, error) {
  const failure = new Error((error && error.message) || 'Network Error');
  failure.config = config;
  failure.request = null;
  failure.isAxiosError = true;
  if (error && (error.name === 'CanceledError' || error.__CANCEL__ || axios.isCancel(error))) {
    failure.__CANCEL__ = true;
    failure.name = 'CanceledError';
  }
  throw failure;
}

function refusalOf(config, status, reason, code = 'upload_error') {
  return answer(config, status, legacyVideoRefusal({ error: { code, details: { reason } } }));
}

/**
 * Answer one axios request to video_upload.php, as axios' own adapter would.
 * @param {object} config the axios request config
 * @param {{http?: object}} [deps]
 */
export async function dispatchVideoRequest(config, deps = {}) {
  const http = deps.http || api.raw;
  const method = String(config.method || 'get').toUpperCase();
  const params = query(config.url);
  const action = params.get('action');

  let request;

  if (action === 'config' && method === 'GET') {
    request = { method: 'GET', url: '/video-uploads/config' };
  } else if (action === 'start' && method === 'POST') {
    const body = jsonBody(config.data);
    const start = {
      domain: body.domain,
      filename: body.filename,
      size: body.size,
      chunk_size: body.chunk_size,
      resume_key: body.resume_key,
    };
    // A note's video is about a patient: the chart id the page holds, which
    // the API resolves inside the token's organization and authorizes.
    if (body.patient_id !== undefined && body.patient_id !== null && body.patient_id !== '') {
      start.patient_id = /^[0-9]+$/.test(String(body.patient_id)) ? Number(body.patient_id) : body.patient_id;
    }
    request = { method: 'POST', url: '/video-uploads', data: start };
  } else if (action === 'chunk' && method === 'POST') {
    const form = config.data;
    const read = (name) => (form && typeof form.get === 'function' ? form.get(name) : null);
    const uploadId = String(read('upload_id') || '');
    const offset = String(read('offset') || '');
    const sha256 = String(read('sha256') || '');
    const chunk = read('chunk');
    if (!UPLOAD_ID.test(uploadId)) return refusalOf(config, 400, 'upload_id');
    if (!/^[0-9]{1,15}$/.test(offset)) return refusalOf(config, 400, 'offset');
    if (!/^[0-9a-fA-F]{64}$/.test(sha256)) return refusalOf(config, 400, 'sha256');
    if (!chunk) return refusalOf(config, 400, 'no_chunk');
    request = {
      method: 'PUT',
      url: `/video-uploads/${uploadId}/chunks/${offset}`,
      data: chunk,
      headers: { 'Content-Type': 'application/octet-stream', 'X-Chunk-SHA256': sha256.toLowerCase() },
      onUploadProgress: config.onUploadProgress,
    };
  } else if (action === 'status' && method === 'GET') {
    const uploadId = String(params.get('upload_id') || '');
    if (!UPLOAD_ID.test(uploadId)) return refusalOf(config, 400, 'upload_id');
    request = { method: 'GET', url: `/video-uploads/${uploadId}` };
  } else if ((action === 'complete' || action === 'discard') && method === 'POST') {
    const body = jsonBody(config.data);
    const uploadId = String(body.upload_id || (action === 'discard' ? body.file_uid : '') || '');
    if (!UPLOAD_ID.test(uploadId)) return refusalOf(config, 400, 'upload_id');
    request = action === 'complete'
      ? { method: 'POST', url: `/video-uploads/${uploadId}/complete`, data: {} }
      : { method: 'DELETE', url: `/video-uploads/${uploadId}` };
  } else if (action && Object.prototype.hasOwnProperty.call(ACTIONS, action)) {
    return refusalOf(config, 405, 'method', 'method_not_allowed');
  } else {
    return refusalOf(config, 404, 'action', 'not_found');
  }

  let res;
  try {
    res = await http.request({
      ...request,
      timeout: config.timeout || 0,
      cancelToken: config.cancelToken,
      signal: config.signal,
      validateStatus: () => true,
    });
  } catch (error) {
    return noAnswer(config, error);
  }

  const headers = {};
  const retryAfter = res.headers && (res.headers['retry-after'] || res.headers['Retry-After']);
  if (retryAfter !== undefined) headers['retry-after'] = String(retryAfter);

  if (res.status >= 200 && res.status < 300) {
    const data = res.data && typeof res.data === 'object' && 'data' in res.data ? res.data.data : res.data;
    // Main's config answer is the policy itself; every other success is {ok: true, ...}.
    return answer(config, 200, action === 'config' ? data : { ok: true, ...(data || {}) }, headers);
  }

  return answer(config, res.status, legacyVideoRefusal(await errorBodyOf(res.data)), headers);
}

const ACTIONS = { config: 'GET', status: 'GET', start: 'POST', chunk: 'POST', complete: 'POST', discard: 'POST' };

let installed = false;
let previousAdapter = null;

/**
 * Put the video translation in front of axios's default adapter. Installed
 * once, from main.js, after the legacy bridge and before anything uploads:
 * `createVideoUploadClient()` is an `axios.create()`, and an instance takes
 * the default adapter as it is when the instance is made.
 */
export function installVideoBridge() {
  if (installed) return;
  previousAdapter = axios.defaults.adapter;
  const next = typeof axios.getAdapter === 'function' ? axios.getAdapter(previousAdapter) : previousAdapter;
  axios.defaults.adapter = (config) => (isVideoUploadRequest(config.url) ? dispatchVideoRequest(config) : next(config));
  installed = true;
}

export function uninstallVideoBridge() {
  if (!installed) return;
  axios.defaults.adapter = previousAdapter;
  installed = false;
}

/**
 * Release an upload the person walked away from: DELETE, as a keepalive fetch
 * with the bearer, so it finishes when the page is going away. Best effort and
 * silent: the API abandons the person's own unreferenced video and leaves a
 * bound one alone.
 * @returns {Promise<boolean>} whether the API took the request
 */
export async function discardVideoUploadRest(uploadId, deps = {}) {
  if (!isFileUid(uploadId)) return false;
  const fetchImpl = deps.fetch || (typeof fetch === 'function' ? (...args) => fetch(...args) : null);
  if (!fetchImpl) return false;
  try {
    const token = deps.token !== undefined ? deps.token : await oidc.getAccessToken();
    if (!token) return false;
    const res = await fetchImpl(`${BASE_URL}/api/v1/video-uploads/${uploadId}`, {
      method: 'DELETE',
      keepalive: true,
      credentials: 'omit',
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    });
    return !!res && res.ok === true;
  } catch (_) {
    return false;
  }
}

/**
 * A stream ticket for a video: the URL a `<video>` element reads every slice
 * from (decision D5 as amended). Issued only after the API's whole read
 * decision; a refusal keeps main's states (409 scanning, 404/410 unavailable,
 * 401 mfa_required, 429 rate limited).
 * @returns {Promise<{ok:true,url:string,expiresIn:number|null}|{ok:false,state:string,code:string|null}>}
 */
export async function issueStreamTicket(uid, deps = {}) {
  if (!isFileUid(uid)) return { ok: false, state: 'unavailable', code: null };
  const http = deps.http || api.raw;
  let res;
  try {
    res = await http.post(`/documents/${uid}/content-tickets`, { purpose: 'stream' }, { validateStatus: () => true });
  } catch (_) {
    return { ok: false, state: 'error', code: null };
  }
  const body = (res && res.data) || {};
  if (res && res.status === 201 && body.data && typeof body.data.url === 'string') {
    return { ok: true, url: body.data.url, expiresIn: Number.isFinite(body.data.expires_in) ? body.data.expires_in : null };
  }
  const code = body.error && typeof body.error.code === 'string' ? body.error.code : null;
  return { ok: false, state: refusalStateFromResponse(res ? res.status : 0, code), code };
}

/**
 * The question the player's element asked, for one byte, with the bearer:
 * whether the API would serve this video now. ok means it would, so a failure
 * of the element is the browser's (a codec or container it cannot decode).
 * @returns {Promise<{ok:true}|{ok:false,state:string,code:string|null}>}
 */
export async function probeVideoContent(uid, deps = {}) {
  if (!isFileUid(uid)) return { ok: false, state: 'unavailable', code: null };
  const http = deps.http || api.raw;
  let res;
  try {
    res = await http.get(`/documents/${uid}/content`, {
      headers: { Range: 'bytes=0-0' },
      responseType: 'blob',
      timeout: deps.timeoutMs || 15000,
      validateStatus: () => true,
    });
  } catch (_) {
    return { ok: false, state: 'error', code: null };
  }
  if (!res || typeof res.status !== 'number') return { ok: false, state: 'error', code: null };
  if (res.status === 200 || res.status === 206) return { ok: true };
  const body = await errorBodyOf(res.data);
  const code = body.error && typeof body.error.code === 'string' ? body.error.code : null;
  return { ok: false, state: refusalStateFromResponse(res.status, code), code };
}

/**
 * A note save through the bridge, for `Note.SavePatientNote` and the flat
 * `SavePatientNote` (src/api2/bridgeMap.js). Main's answer, as notesApi.saveNote
 * reads it: msg 'Inserted' and the note's id on every save, and for a save that
 * named videos `attachments_unbound` (empty: the port binds a note's videos in
 * the note's own transaction, all or none) and the `attachments` now on it.
 *
 * A save that named videos and was refused keeps its REAL status and main's
 * body, as main's keyon answers it, because notesApi reads a 4xx or 5xx as
 * "refused, with the server's sentence" - and a 409 `already_attached` or a
 * 401 `mfa_required` must not read as "failed" or, worse, as saved. A save
 * with no video keeps the operation's own error style.
 *
 * @param {string} path `/patients/{id}/notes`
 * @param {object} body the REST body (noteBody)
 * @param {object} p the legacy payload
 * @param {{http?: object}} [deps]
 */
export async function saveNoteWithVideos(path, body, p, deps = {}) {
  const videos = Array.isArray(p && p.attachment_uids) && p.attachment_uids.length > 0;
  if (!videos) {
    const note = await (deps.api || api).post(path, body);
    return { msg: 'Inserted', id: note && note.id, note_id: note && note.id };
  }

  const http = deps.http || api.raw;
  let res;
  try {
    res = await http.post(path, body, { validateStatus: () => true });
  } catch (error) {
    const failure = new Error((error && error.message) || 'Network Error');
    failure.isAxiosError = true;
    throw failure;
  }

  if (res.status >= 200 && res.status < 300) {
    const note = (res.data && res.data.data) || {};
    return {
      msg: 'Inserted',
      id: note.id,
      note_id: note.id,
      attachments_unbound: [],
      attachments: Array.isArray(note.attachments) ? note.attachments : [],
    };
  }

  const refusal = new Error(`Request failed with status code ${res.status}`);
  refusal.isAxiosError = true;
  refusal.response = {
    status: res.status,
    data: legacyVideoRefusal(await errorBodyOf(res.data)),
    headers: res.headers || {},
  };
  throw refusal;
}

export default {
  isVideoUploadRequest,
  dispatchVideoRequest,
  installVideoBridge,
  discardVideoUploadRest,
  issueStreamTicket,
  probeVideoContent,
  saveNoteWithVideos,
  legacyVideoRefusal,
};
